import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import BottomSheet from '../BottomSheet';
import { useAuth } from '../../contexts/AuthContext';
import { useFamily } from '../../contexts/FamilyContext';
import toast from 'react-hot-toast';

export default function DiscussCardDetailModal({ card, experience, onClose, onUpdate }) {
  const { user } = useAuth();
  const { family } = useFamily();
  const [newComment, setNewComment] = useState('');
  const [assignees, setAssignees] = useState(card.assignedTo || []);

  const getUserName = (userId) => {
    if (!userId || !family?.members) return 'Unknown';
    if (userId === user?.uid) return 'You';
    return family.onboarding?.partnerName || `Family Member`;
  };

  const getUserInitials = (userId) => {
    const name = getUserName(userId);
    return name.charAt(0).toUpperCase();
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    const comment = {
      userId: user.uid,
      userName: getUserName(user.uid),
      text: newComment,
      timestamp: new Date()
    };

    const updatedComments = [...(card.comments || []), comment];
    
    await onUpdate({ comments: updatedComments });
    
    // Notify other family members (would integrate with notification system)
    // notifyFamilyMembers(experience.id, `${user.displayName} commented on "${card.title}": ${newComment}`);

    setNewComment('');
    toast.success('Comment added');
  };

  const handleApprove = async () => {
    const approvedBy = card.approvedBy || [];
    
    if (approvedBy.includes(user.uid)) {
      // Unapprove
      await onUpdate({ 
        approvedBy: approvedBy.filter(id => id !== user.uid)
      });
      toast.success('Approval removed');
    } else {
      // Approve
      await onUpdate({ 
        approvedBy: [...approvedBy, user.uid]
      });
      toast.success('Approved!');

      // Check if all required approvals are met
      if (card.approvalRequired?.length > 0) {
        const allApproved = card.approvalRequired.every(id => 
          [...approvedBy, user.uid].includes(id)
        );
        
        if (allApproved) {
          toast.success('Decision approved by everyone!');
          // Auto-move to "Decided" column
          await onUpdate({ 
            status: 'decided',
            kanbanColumn: 'decided',
            approvedBy: [...approvedBy, user.uid]
          });
        }
      }
    }
  };

  const handleAssignTask = async (userId) => {
    const updated = assignees.includes(userId)
      ? assignees.filter(id => id !== userId)
      : [...assignees, userId];
    
    setAssignees(updated);
    await onUpdate({ assignedTo: updated });
    toast.success('Task assignment updated');
  };

  const isApproved = card.approvedBy?.includes(user.uid);
  const needsMyApproval = card.approvalRequired?.includes(user.uid);

  return (
    <BottomSheet isOpen={true} onClose={onClose} title={card.title} fullScreen={true}>
      <div className="p-6 space-y-8">
        {/* Decision content (from Explore phase) */}
        <div className="bg-gray-50 rounded-xl p-6">
          <h3 className="font-bold text-text-primary mb-4">Current Selection</h3>
          
          {card.type === 'gallery' && card.gallery?.length > 0 && (
            <div className="grid grid-cols-3 gap-3">
              {card.gallery
                .filter(img => img.rating > 0)
                .map((img, i) => (
                  <img key={i} src={img.url} alt="" className="w-full aspect-square object-cover rounded-lg" />
                ))}
            </div>
          )}

          {card.type === 'options' && card.selectedOption && (
            <p className="font-semibold text-primary">{card.selectedOption}</p>
          )}

          {card.type === 'text' && card.textValue && (
            <p className="text-text-primary">{card.textValue}</p>
          )}

          {card.type === 'date' && card.selectedDate && (
            <p className="font-semibold text-primary">
              {new Date(card.selectedDate).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Approval section */}
        {needsMyApproval && (
          <div className="space-y-4">
            <h3 className="font-bold text-text-primary">Your Approval</h3>
            
            <button
              onClick={handleApprove}
              className={`
                w-full py-4 rounded-xl font-bold transition-all
                ${isApproved 
                  ? 'bg-green-100 text-green-700 border-2 border-green-300' 
                  : 'bg-white border-2 border-border hover:border-primary'
                }
              `}
            >
              {isApproved ? '✓ Approved' : 'Approve This Decision'}
            </button>

            {/* Show other approvals */}
            <div className="flex items-center gap-3">
              {card.approvalRequired?.map(userId => {
                const approved = card.approvedBy?.includes(userId);
                return (
                  <div key={userId} className="flex items-center gap-2">
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                      ${approved ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}
                    `}>
                      {approved ? '✓' : getUserInitials(userId)}
                    </div>
                    <span className="text-sm text-text-secondary">
                      {getUserName(userId)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Task assignment */}
        {experience.planningParticipants && experience.planningParticipants.length > 0 && (
          <div className="space-y-4">
            <h3 className="font-bold text-text-primary">Assign Tasks</h3>
            
            <div className="space-y-2">
              {experience.planningParticipants.map(userId => (
                <button
                  key={userId}
                  onClick={() => handleAssignTask(userId)}
                  className={`
                    w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between
                    ${assignees.includes(userId)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary'
                    }
                  `}
                >
                  <span className="font-semibold">{getUserName(userId)}</span>
                  {assignees.includes(userId) && (
                    <span className="text-primary">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Comments */}
        <div className="space-y-4">
          <h3 className="font-bold text-text-primary">Discussion</h3>
          
          {/* Comment list */}
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {card.comments?.map((comment, i) => {
              const commentDate = comment.timestamp?.toDate 
                ? comment.timestamp.toDate() 
                : (comment.timestamp ? new Date(comment.timestamp) : new Date());
              
              return (
                <div key={i} className="bg-white rounded-xl p-4 border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                      {comment.userName?.[0] || getUserInitials(comment.userId)}
                    </div>
                    <div>
                      <p className="font-semibold text-text-primary text-sm">{comment.userName || getUserName(comment.userId)}</p>
                      <p className="text-xs text-text-secondary">
                        {formatDistanceToNow(commentDate, { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                  <p className="text-text-primary">{comment.text}</p>
                </div>
              );
            })}
          </div>

          {/* Add comment */}
          <div className="flex gap-3">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add your thoughts..."
              rows={3}
              className="flex-1 px-4 py-3 border-2 border-border rounded-xl resize-none"
            />
            <button
              onClick={handleAddComment}
              className="px-6 bg-primary text-white font-bold rounded-xl hover:bg-primary-dark transition-colors"
            >
              Send
            </button>
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="w-full bg-white border-2 border-border text-text-primary font-bold py-4 rounded-xl"
        >
          Close
        </button>
      </div>
    </BottomSheet>
  );
}
